import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Sources stay deferred (`data-video-source`) with `preload="metadata"` and a
 * capped poster until the element scrolls into view; offscreen playback is
 * paused so background video never burns CPU or bandwidth unseen.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #sourcesAttached = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;

    if (typeof IntersectionObserver === 'undefined') {
      this.#attachSources();
      videoElement.load();
      return;
    }

    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      rootMargin: '200px',
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect(entries) {
    const entry = entries.find((candidate) => candidate.target === this);
    if (!entry) return;

    const { videoElement } = this.refs;

    if (entry.isIntersecting) {
      if (!this.#sourcesAttached) {
        this.#attachSources();
        videoElement.load();
      }
      videoElement.play().catch(() => {});
    } else {
      videoElement.pause();
    }
  }

  #attachSources() {
    if (this.#sourcesAttached) return;

    const { videoSources } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    this.#sourcesAttached = true;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
